import { EditIcon, RightArrowIcon } from 'assets/icons';
import { EditNew, EditTimeIcon } from 'assets/icons/';
import cx from 'classnames';
import Icon from 'components/atoms/Icon';
import type { IconNames } from 'components/atoms/Icon/Icon.styles';
import type { PartnerIcon } from 'config/types';
import i18n from 'i18n/config';
import React, { type ReactNode, useMemo } from 'react';
import ListGroup from 'react-bootstrap/ListGroup';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getPartnerSpecificComponent } from 'utils/helpers';

import { OptionItemLoader } from '../Loaders';
import OptionTitle from '../OptionTitle';
import styles from './styles.module.scss';

export type OptionItemIconType = 'edit' | 'timeEdit' | 'open';

export interface OptionsProps {
  title: string | JSX.Element;
  actionType?: OptionItemIconType;
  value?: string | JSX.Element | JSX.Element[];
  disabled?: boolean;
  isEditable?: boolean;
  onClick?: () => any;
  className?: string;
  readonly?: boolean;
  danger?: boolean;
  isPreference?: boolean;
  loading?: boolean;
  startIconName?: IconNames;
  startIcon?: JSX.Element;
  rightIcon?: JSX.Element;
  hasFixedStyle?: boolean;
  hideItemSeparator?: boolean;
  hasRightArrowOnly?: boolean;
  description?: string;
  footer?: ReactNode;
  subTitleValueColor?: string;
  hasNoActiveState?: boolean;
}

const StyledP = styled.p<{ style?: any }>`
  color: ${({ theme, style }) => (style.color ? style.color : theme.components.optionItem.color)};
`;

const StyledIcon = styled(Icon)<{ isDanger?: boolean }>`
  .path1:before {
    color: ${({ theme, isDanger = false }) =>
      isDanger ? theme.components.optionItem.leftIcon.danger.color : theme.components.optionItem.leftIcon.color};
  }

  .path2:before {
    color: ${({ theme, isDanger = false }) =>
      isDanger ? theme.components.optionItem.leftIcon.danger.color : theme.components.optionItem.leftIcon.color};
  }

  .path3:before {
    color: ${({ theme, isDanger = false }) =>
      isDanger ? theme.components.optionItem.leftIcon.danger.color : theme.components.optionItem.leftIcon.color};
  }
`;

const StyledContainerLeftIcon = styled.div`
  width: 37px;
  margin-right: 17px;
  padding-top: 2px;
`;

const StyledTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: 3px;
  margin-right: 3px;
  overflow: auto;
  width: 100%;
`;

const StyledEditNew = styled(EditNew)`
  > path {
    fill: ${({ theme }) => theme.components.optionItem.rightIcon.color};
  }
`;
const StyledEditTimeIcon = styled(EditTimeIcon)`
  > path {
    fill: ${({ theme }) => theme.components.optionItem.rightIcon.color};
  }
`;
const StyledRightArrowIcon = styled(RightArrowIcon)`
  g > g {
    stroke: ${({ theme }) => theme.components.optionItem.rightIcon.color};
  }
`;

const StyledEditIcon = styled(EditIcon)`
  g > path {
    fill: ${({ theme }) => theme.components.optionItem.rightIcon.color};
  }
  margin-top: 6px;
`;

const editIcons: PartnerIcon<any> = {
  INSTASHOP: StyledEditNew,
  CAREEM: StyledEditNew,
  Default: StyledEditIcon,
};

const PartnerSpecificEditIcon = getPartnerSpecificComponent(editIcons);

const OptionItem: React.FC<OptionsProps> = ({
  title,
  actionType,
  description,
  value,
  readonly = false,
  disabled = readonly,
  isEditable = true,
  hideItemSeparator = false,
  className,
  isPreference = false,
  danger = false,
  onClick = () => {},
  loading = false,
  startIconName,
  hasFixedStyle = false,
  hasRightArrowOnly = false,
  footer = null,
  startIcon,
  rightIcon,
  subTitleValueColor,
  hasNoActiveState,
}) => {
  const { t } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  const renderIcon = useMemo(() => {
    if (disabled) return null;
    switch (actionType) {
      case 'edit':
        return rightIcon || <PartnerSpecificEditIcon className={isRTL ? 'transform-270' : ''} />;
      case 'timeEdit':
        return rightIcon || <StyledEditTimeIcon />;
      case 'open':
        return rightIcon || <StyledRightArrowIcon className={isRTL ? 'transform-180' : ''} />;
      default:
        return null;
    }
  }, [actionType, disabled, isRTL, rightIcon]);

  const renderDescription = () => {
    if (!value) {
      return null;
    }

    if (!description) {
      return null;
    }

    return (
      <StyledP style={{ color: subTitleValueColor }} className={cx('mb-0 h4 font-weight-light text-truncate mt-3', styles.text, styles.description)}>
        {t(description)}
      </StyledP>
    );
  };

  const rightArrow = useMemo(
    () => ((isEditable && (isPreference || !!value) && !disabled) || hasRightArrowOnly) && <div className="margin-start-auto pt-1">{renderIcon}</div>,
    [isEditable, isPreference, value, disabled, hasRightArrowOnly, renderIcon]
  );

  return (
    <ListGroup.Item
      className={cx('px-0', className, {
        [styles.disabled]: disabled,
        [styles.readonly]: readonly,
        [styles.danger]: danger,
        'border-bottom-0': hideItemSeparator,
      })}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {loading && <OptionItemLoader />}
      {!loading && (
        <>
          <div className={cx('d-flex')}>
            {startIconName && typeof startIconName === 'string' ? (
              <StyledContainerLeftIcon>
                <StyledIcon
                  isDanger={danger}
                  className={startIconName}
                  isActive={!disabled && !value}
                  isDisabled={disabled}
                  hasFixedStyle={hasFixedStyle}
                />
              </StyledContainerLeftIcon>
            ) : (
              startIcon && <StyledContainerLeftIcon>{startIcon}</StyledContainerLeftIcon>
            )}
            <StyledTitleContainer>
              <OptionTitle title={title} isActive={!value && !isPreference && !hasNoActiveState} className={cx('mb-0', styles.title)} />
              <StyledP style={{ color: subTitleValueColor }} className={cx('mb-0 h4 font-weight-light text-truncate', styles.text)}>
                {value}
              </StyledP>
            </StyledTitleContainer>
            {rightArrow}
          </div>
          {renderDescription()}
          <div className={cx(styles.description)}>{footer}</div>
        </>
      )}
    </ListGroup.Item>
  );
};

export default OptionItem;
